import { supabase } from '../../supabase';
import { callEdgeFunction } from '../authService';

/**
 * Redeem a recovery code (the "lost authenticator" path). The edge function
 * verifies the code, consumes it, and clears the user's TOTP factors so they can
 * re-enroll. Returns the raw edge result: { success, factors_removed } on
 * success, or { error, status } on failure (callEdgeFunction normalizes both).
 */
export async function redeemRecoveryCode(code, locale) {
  const result = await callEdgeFunction('redeem-mfa-recovery-code', { code: String(code ?? '').trim(), locale: locale || 'en' });
  if (!result?.error) {
    // The edge fn cleared the TOTP factor server-side, but the current session's
    // AAL claim is stale (still "second factor expected"). Without a refresh,
    // ProtectedRoute / the login MFA check re-evaluate against the stale claim
    // and bounce the user straight back to the (now factor-less) MFA screen — an
    // infinite loop. Re-mint the token so every downstream check sees aal1 / no
    // factor and lets the user in to re-enroll. Awaited so it completes before
    // the caller fires onSuccess + navigates. Non-fatal.
    try {
      await supabase.auth.refreshSession();
    } catch (_e) {
      /* non-fatal — worst case the user signs out and back in */
    }
  }
  return result;
}

/**
 * RD-SEC-015 — server-generated recovery codes via the `mfa-recovery-codes`
 * edge function. The endpoint requires a FRESH TOTP step-up (a
 * `challengeAndVerify` in the last 5 minutes), generates 10 codes
 * server-side, persists only their SHA-256 hashes (`replace_mfa_recovery_codes`,
 * SECURITY DEFINER), and returns the plaintext once for display. This is the
 * enrollment stepper's ONLY path to recovery codes — the client no longer
 * generates codes itself (see `src/utils/auth/mfaRecoveryCodes.js`'s header:
 * generation is server-only now, that module keeps only the normalize/format/
 * hash helpers a client still needs).
 *
 * @returns {Promise<{codes?: string[], error?: string, status?: number}>}
 */
export async function issueRecoveryCodes() {
  return callEdgeFunction('mfa-recovery-codes', {});
}

/**
 * RD-SEC-008 Phase B — Email recovery. Request a one-time code emailed to the
 * user's account address (no auth required beyond the current session token).
 */
export async function requestEmailRecovery(locale) {
  return callEdgeFunction('request-mfa-email-recovery', { locale: locale || 'en' });
}

/**
 * Redeem the emailed one-time code. Clears the user's TOTP factor server-side
 * so they can re-enroll. Mirrors redeemRecoveryCode's session-refresh behaviour —
 * same stale-AAL-claim reason applies (see that fn's comment).
 */
export async function redeemEmailRecovery(code, locale) {
  const result = await callEdgeFunction('redeem-mfa-email-recovery', { code: String(code ?? '').trim(), locale: locale || 'en' });
  if (!result?.error) {
    // The edge fn cleared the TOTP factor server-side, but the current session's
    // AAL claim is stale. Re-mint the token so downstream checks see aal1 / no
    // factor and let the user in to re-enroll. Non-fatal.
    try { await supabase.auth.refreshSession(); } catch (_e) { /* non-fatal */ }
  }
  return result;
}

/** Count of UNUSED recovery codes for the user (drives the "N remaining" hint). */
export async function countUnusedRecoveryCodes(userId) {
  if (!userId) return 0;
  const { count, error } = await supabase
    .from('mfa_recovery_codes')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .is('used_at', null);
  if (error) throw error;
  return count ?? 0;
}
